import { z } from 'zod';
import {
  COST_TYPES,
  EXPENSE_UNITS,
  FILE_KINDS,
  FILE_MIME_TYPES,
  MAX_FILE_BYTES,
  PAYMENT_METHODS,
  SALE_TYPES,
} from '../enums';
import { PHONE_E164_REGEX } from '../phone';
import { clientCreatedAt, isoDate, optText, santim, text, uuid, type SyncFields } from './common';

/** IN-1 */
export const buyerSchema = z.object({
  name: text(60),
  phone: z.string().regex(PHONE_E164_REGEX).nullish(),
  pricePerLiterSantim: z.number().int().min(100).max(100_000),
  active: z.boolean().default(true),
  note: optText(500),
  clientCreatedAt,
});
export type BuyerData = z.output<typeof buyerSchema>;
export type BuyerRecord = BuyerData & SyncFields;

/** IN-2, IN-5 */
export const saleSchema = z
  .object({
    type: z.enum(SALE_TYPES),
    date: isoDate,
    buyerId: uuid.nullish(),
    milkMl: z.number().int().nullish(),
    unitPriceSantim: z.number().int().nullish(),
    amountSantim: santim(1),
    animalId: uuid.nullish(),
    description: optText(200),
    clientCreatedAt,
  })
  .superRefine((s, ctx) => {
    if (s.type === 'MILK') {
      if (!s.buyerId) ctx.addIssue({ code: 'custom', path: ['buyerId'], message: 'Required' });
      if (s.milkMl === null || s.milkMl === undefined || s.milkMl < 100 || s.milkMl > 2_000_000)
        ctx.addIssue({ code: 'custom', path: ['milkMl'], message: '0.1–2,000 L' });
      if (s.unitPriceSantim === null || s.unitPriceSantim === undefined || s.unitPriceSantim < 1)
        ctx.addIssue({ code: 'custom', path: ['unitPriceSantim'], message: 'Required' });
    } else {
      if (s.milkMl != null) ctx.addIssue({ code: 'custom', path: ['milkMl'], message: 'Milk sales only' });
      if (s.unitPriceSantim != null)
        ctx.addIssue({ code: 'custom', path: ['unitPriceSantim'], message: 'Milk sales only' });
    }
    if (s.type === 'ANIMAL' && !s.animalId)
      ctx.addIssue({ code: 'custom', path: ['animalId'], message: 'Required' });
    if (s.type !== 'ANIMAL' && s.animalId)
      ctx.addIssue({ code: 'custom', path: ['animalId'], message: 'Animal sales only' });
    if (s.type === 'OTHER' && !s.description)
      ctx.addIssue({ code: 'custom', path: ['description'], message: 'Required' });
  });
export type SaleData = z.output<typeof saleSchema>;
export type SaleRecord = SaleData & SyncFields;

/** IN-3 */
export const paymentSchema = z.object({
  buyerId: uuid,
  date: isoDate,
  amountSantim: santim(1),
  method: z.enum(PAYMENT_METHODS),
  reference: optText(100),
  saleId: uuid.nullish(),
  clientCreatedAt,
});
export type PaymentData = z.output<typeof paymentSchema>;
export type PaymentRecord = PaymentData & SyncFields;

/** EX-2 */
export const expenseCategorySchema = z.object({
  name: text(60),
  nameAm: text(60),
  parentId: uuid.nullish(),
  costType: z.enum(COST_TYPES),
  isFeed: z.boolean().default(false),
  /** Seeded categories only; set by the server, never by a client. */
  systemKey: z.string().max(60).nullish(),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
  clientCreatedAt,
});
export type ExpenseCategoryData = z.output<typeof expenseCategorySchema>;
export type ExpenseCategoryRecord = ExpenseCategoryData & SyncFields;

/** EX-1, EX-3 */
export const expenseSchema = z.object({
  date: isoDate,
  categoryId: uuid,
  description: optText(200),
  quantity: z.number().positive().max(1_000_000).nullish(),
  unit: z.enum(EXPENSE_UNITS).nullish(),
  amountSantim: santim(1),
  coversMonths: z.number().int().min(1).max(12).default(1),
  paidTo: optText(80),
  method: z.enum(PAYMENT_METHODS),
  animalId: uuid.nullish(),
  sourceEventId: uuid.nullish(),
  recurringExpenseId: uuid.nullish(),
  receiptFileId: uuid.nullish(),
  clientCreatedAt,
});
export type ExpenseData = z.output<typeof expenseSchema>;
export type ExpenseRecord = ExpenseData & SyncFields;

/** EX-4 */
export const recurringExpenseSchema = z
  .object({
    categoryId: uuid,
    description: text(200),
    amountSantim: santim(1),
    dayOfMonth: z.number().int().min(1).max(28),
    method: z.enum(PAYMENT_METHODS),
    paidTo: optText(80),
    startMonth: isoDate,
    endMonth: isoDate.nullish(),
    active: z.boolean().default(true),
    clientCreatedAt,
  })
  .superRefine((r, ctx) => {
    if (!r.startMonth.endsWith('-01'))
      ctx.addIssue({ code: 'custom', path: ['startMonth'], message: 'Must be the 1st of a month' });
    if (r.endMonth) {
      if (!r.endMonth.endsWith('-01'))
        ctx.addIssue({ code: 'custom', path: ['endMonth'], message: 'Must be the 1st of a month' });
      if (r.endMonth < r.startMonth)
        ctx.addIssue({ code: 'custom', path: ['endMonth'], message: 'Before the start month' });
    }
  });
export type RecurringExpenseData = z.output<typeof recurringExpenseSchema>;
export type RecurringExpenseRecord = RecurringExpenseData & SyncFields;

/** PL-13 */
export const fileAssetSchema = z.object({
  kind: z.enum(FILE_KINDS),
  mimeType: z.enum(FILE_MIME_TYPES),
  sizeBytes: z.number().int().min(1).max(MAX_FILE_BYTES),
  clientCreatedAt,
});
export type FileAssetData = z.output<typeof fileAssetSchema>;
export type FileAssetRecord = FileAssetData & SyncFields & { uploadedAt?: string | null };
